<template>
  <VModal v-if="showing" showing overflow-content :bordered-footer="true" @update:showing="$emit('update:showing', $event)">
    <template #header>
      <h2 class="text-lg mb-0">{{ duplicateToDifferentCompany ? 'Duplicate Deck to Different Company' : 'Duplicate Deck' }}</h2>
    </template>

    <div class="flex flex-col gap-8 w-screen max-w-screen-sm">
      <FormField v-if="duplicateToDifferentCompany" label="Company Name" required>
        <VDropdown
          v-model="state.company"
          :options="managedCompanies"
          placeholder="Select Company..."
          class="placeholder-gray-400"
          @input="state.companyError = null"
        >
          <template #current-single="{ item }">
            {{ item.name }}
          </template>
          <template #option="{ item, selected }">
            <VDropdownItem
              :item="item.name"
              :selected="(item as any).selected"
              :class="{ 'text-gray-900 font-semibold': selected }"
              :data-testid="`dropdown-item-$item-id}`"
            />
          </template>
        </VDropdown>
        <p v-if="state.companyError" class="error text-xs text-red-800 mt-1" data-testid="deck-company-error">
          {{ state.companyError }}
        </p>
      </FormField>
      <FormField label="Title of Duplicated Deck" required>
        <VInput
          v-model="state.title"
          name="deck-title"
          data-testid="duplicate-deck-title-input"
          :maxlength="128"
          :error="deckHasV1Models"
          required
          @input="state.titleError = null"
        />
        <p v-if="state.titleError" class="error text-sm text-red-800 mt-2" data-testid="deck-title-error">
          {{ state.titleError }}
        </p>
        <p v-if="deckHasV1Models" class="error text-sm text-red-800 mt-2">
          <span class="font-bold">Error</span>: This deck contains an older version of a 3D model. Please reach out to support@vrify.com to
          update this model.
        </p>
        <div v-if="duplicateToDifferentCompany" class="mt-4">
          <p v-if="deckHasUnprocessedData" class="error text-sm text-red-800">
            <span class="font-bold">Error</span>: Cannot duplicate drill data since data is still being processed. Try duplicating drill
            data again later.
          </p>
          <p v-else>3D Models, projects, and drill data will automatically be duplicated with a new ID</p>
        </div>
        <div v-else-if="isInternalAdmin" class="flex flex-col mt-4">
          <div class="option mb-2" :class="{ disabled: deckHasV1Models }">
            <VCheckbox id="duplicate-models" v-model="state.duplicateModelFlag" class="mr-2" :disabled="deckHasV1Models" />
            <label class="m-0 text-sm" for="duplicate-models">Duplicate 3D models</label>
          </div>
          <div class="option" :class="{ disabled: !state.duplicateModelFlag || deckHasUnprocessedData }">
            <VCheckbox
              id="duplicate-drill-data"
              v-model="state.duplicateDrillDataFlag"
              class="mr-2"
              :disabled="!state.duplicateModelFlag || !!deckHasUnprocessedData"
            />
            <label class="m-0 text-sm" for="duplicate-drill-data">Duplicate drill data</label>
          </div>
          <div v-if="deckHasUnprocessedData" class="text-sm mt-2 text-gray-600">
            Cannot duplicate drill data since data is still being processed. Try duplicating drill data again later.
          </div>
        </div>
      </FormField>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <VButton data-testid="back-btn" @click="$emit('update:showing', false)"> Back </VButton>
        <VButton data-testid="duplicate-deck-btn" variant="copper" :disabled="shouldDisableDuplicateButton" @click="duplicateDeck">
          Create
        </VButton>
      </div>
    </template>
  </VModal>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { computed, reactive } from 'vue'
import { VButton, VCheckbox, VModal } from '@vrifytechnology/vrify-component-library'

import type { EditDeck } from '@/composables/useEditDeck'
import type { User } from '@/entities/user'
import type { Company } from '@/entities/company'
import type { CameraData } from '~/entities/model3d'

const props = defineProps({
  deck: {
    type: Object as PropType<DeepReadonly<EditDeck>>,
    required: true,
  },
  showing: {
    type: Boolean,
    default: false,
  },
  duplicateToDifferentCompany: {
    type: Boolean,
    default: false,
  },
  user: {
    type: Object as PropType<DeepReadonly<User>>,
    default: () => {},
  },
  threeDViewer: {
    type: Object as PropType<UseThreeDViewer>,
    required: true,
  },
  priorityCameras: {
    type: Array as PropType<CameraData[]>,
    default: () => [],
  },
})

const emit = defineEmits(['add-slide', 'error', 'compose-new-scene', 'update:showing', 'duplicate-deck'])

const state = reactive({
  company: null as Company | null,
  companyError: null as string | null,
  deckId: props.deck.id,
  duplicateModelFlag: false,
  duplicateDrillDataFlag: false,
  title: `Copy of ${props.deck.title}`,
  titleError: null as string | null,
})
const { hasRole } = useUser()
const isInternalAdmin = computed(() => hasRole('internal_admin'))

const managedCompanies = computed(() => {
  return props.user?.companies.filter((company: Company) => props.deck.company.id !== company.id) ?? []
})

const loading = ref(false)
const deckHasUnprocessedData = ref(false)
const deckHasV1Models = computed(() => props.threeDViewer.threeDRepo?.deckHasV1Models())
const shouldDisableDuplicateButton = computed(
  () => loading.value || deckHasV1Models.value || (props.duplicateToDifferentCompany && deckHasUnprocessedData.value),
)

const deck3dModels = computed(() => [...new Set(props.priorityCameras.map((camera: CameraData) => camera.model3d_id))])
async function checkForUnprocessedDatasets(): Promise<boolean> {
  const { drillDataSetManager } = props.threeDViewer.world
  if (drillDataSetManager == null) return false
  const requests = deck3dModels.value.map(modelId => {
    return drillDataSetManager.getDataSets({ companyId: props.deck.company.id, modelId })
  })
  const responses = await Promise.all(requests)
  const dataSets = responses.flat()
  return dataSets.some(dataSet => dataSet.data_set.hole_data_status !== 'ready')
}

onMounted(async () => {
  if (deck3dModels.value.length) {
    loading.value = true
    deckHasUnprocessedData.value = await checkForUnprocessedDatasets()
    loading.value = false
  }
})

function duplicateDeck() {
  validate()
  if (state.titleError || state.companyError) return

  const payload = {
    title: state.title,
    deckId: state.deckId,
  }

  const options = {
    duplicateModel: props.duplicateToDifferentCompany || state.duplicateModelFlag,
    duplicateDrillData: state.duplicateDrillDataFlag,
    company: state.company ?? undefined,
  }

  emit('duplicate-deck', payload, options)
  emit('update:showing', false)
}

watch(
  () => state.duplicateModelFlag,
  () => {
    if (!state.duplicateModelFlag) {
      state.duplicateDrillDataFlag = false
    }
  },
)

function validate() {
  if (!state.title) {
    state.titleError = 'Please add a title for this duplicated deck'
  }

  if (props.duplicateToDifferentCompany && !state.company) {
    state.companyError = 'Please select a company to duplicate this deck to'
  }
}
</script>
<style lang="stylus" scoped>
.option
  @apply flex flex-row items-center
  &.disabled
    @apply text-gray-600
</style>
